import { z } from 'zod';

export const SCartProduct = z.object({
  id: z.number(),
  cart_id: z.string(),
  product_name: z.string(),
  product_type: z.string(),
  product_category: z.string(),
  prescription_number: z.string(),
  price: z.string(),
  percent_tax: z.number(),
  is_ethical: z.boolean(),
  unit_name: z.string(),
  doctor_discount_type: z.string(),
  doctor_discount_value: z.string(),
  faskes_discount_type: z.string(),
  faskes_discount_value: z.string(),
  qty_order: z.number(),
  branch_id: z.number(),
  bp_id: z.number(),
  bpi_id: z.number(),
  bpip_id: z.number(),
  is_paid: z.boolean(),
  services_type: z.string(),
  is_selected: z.boolean(),
  product_image: z.array(z.string()).nullable(),
  minimum_order: z.number().optional(),
});

export const SCartService = z.object({
  id: z.number(),
  cart_id: z.string(),
  individual_id: z.number(),
  service_id: z.number(),
  individual_service: z.number(),
  service_name: z.string(),
  doctor_name: z.string(),
  prescription_number: z.string(),
  price: z.string(),
  percent_tax: z.number(),
  doctor_discount_type: z.string(),
  doctor_discount_value: z.string(),
  faskes_discount_type: z.string(),
  faskes_discount_value: z.string(),
  qty_order: z.number(),
  branch_id: z.number(),
  bp_id: z.number(),
  bpi_id: z.number(),
  bpis_id: z.number(),
  is_paid: z.boolean(),
  services_type: z.string(),
  service_method: z.string(),
  is_selected: z.boolean(),
  date: z.string().optional(),
  start: z.string().optional(),
  end: z.string().optional(),
});

export const SCartProcedure = z.object({
  id: z.number(),
  cart_id: z.string(),
  procedure_name: z.string(),
  procedure_id: z.number(),
  prescription_number: z.string(),
  price: z.string(),
  qty_order: z.number(),
  percent_tax: z.number(),
  branch_id: z.number(),
  bp_id: z.number(),
  bpi_id: z.number(),
  is_paid: z.boolean(),
  services_type: z.string(),
  is_selected: z.boolean(),
  doctor_discount_type: z.string(),
  doctor_discount_value: z.string(),
  faskes_discount_type: z.string(),
  faskes_discount_value: z.string(),
  allow_without_consultation: z.boolean(),
});

export const SCartBundle = z.object({
  id: z.number(),
  cart_id: z.string(),
  bundle_origin: z.string(),
  bundle_id: z.number(),
  bundle_name: z.string(),
  price: z.string(),
  items_count: z.number(),
  qty_order: z.number(),
  bundle_code: z.string(),
  bp_id: z.number(),
  bpi_id: z.number(),
  is_paid: z.boolean(),
  services_type: z.string(),
  is_selected: z.boolean(),
  doctor_discount_type: z.string(),
  doctor_discount_value: z.string(),
  faskes_discount_type: z.string(),
  faskes_discount_value: z.string(),
});

export const SCartItem = z.object({
  cart_id: z.string(),
  cart_type: z.string(),
  user_id: z.number(),
  destination_id: z.number(),
  tax: z.number(),
  discount: z.number(),
  is_online: z.boolean(),
  outlet_name: z.string(),
  outlet_logo: z.string(),
  fee_doku: z.number(),
  gross_price: z.number(),
  final_price: z.number(),
  detail: z.object({
    product: z.array(SCartProduct),
    service: z.array(SCartService),
    procedure: z.array(SCartProcedure),
    bundle: z.array(SCartBundle),
  }),
});

export const SCartResponse = z.object({
  data: z.array(SCartItem),
  error: z.object({
    status: z.boolean(),
    msg: z.string(),
    code: z.number(),
  }),
});

export type TCartProduct = z.infer<typeof SCartProduct>;
export type TCartService = z.infer<typeof SCartService>;
export type TCartProcedure = z.infer<typeof SCartProcedure>;
export type TCartBundle = z.infer<typeof SCartBundle>;
export type TCartDetailItem =
  | TCartProduct
  | TCartService
  | TCartProcedure
  | TCartBundle;
export type TCartItem = z.infer<typeof SCartItem>;
export type TCartResponse = z.infer<typeof SCartResponse>;

export const SExpeditionService = z.object({
  services_type: z.string(),
  service_label: z.string(),
  estimate: z.string(),
  rates: z.number(),
  is_cod: z.boolean(),
  additional_fee: z.number().nullable(),
  outlet_name: z.string().optional(),
  outlet_address: z.string().optional(),
  outlet_phone: z.string().optional(),
  outlet_google_maps_url: z.string().optional(),
});

export const SExpedition = z.object({
  expedition_name: z.string(),
  expedition_services: z.array(SExpeditionService),
  insurance_fee: z.number().nullable(),
});

export const SExpeditionItem = z.object({
  name: z.string(),
  description: z.string(),
  quantity: z.number(),
  price: z.number(),
  dimensions: z.object({
    height: z.number(),
    width: z.number(),
    depth: z.number(),
    weight: z.number(),
  }),
});

export const SExpeditionResponse = z.object({
  items: z.array(SExpeditionItem),
  expedition: z.array(SExpedition),
});

export type TExpeditionService = z.infer<typeof SExpeditionService>;
export type TExpedition = z.infer<typeof SExpedition>;
export type TExpeditionItem = z.infer<typeof SExpeditionItem>;
export type TExpeditionResponse = z.infer<typeof SExpeditionResponse>;
